import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import useLocalStorageState from 'use-local-storage-state';
import css from './context-bar.module.scss';

const STORAGE_KEY = 'quodoc_context_bar_width' as const;
const MANUAL_STORAGE_KEY = 'quodoc_context_bar_manual' as const;
const WIDTH_STEP = 16;
const OVERFLOW_TOLERANCE = 1;

type Args = {
  ref: React.RefObject<HTMLElement>;
  minWidth?: number;
  isActive?: boolean;
};

export const MIN_WIDTHS = {
  DEFAULT: 320,
  LARGE: 450,
} as const;

export function useResizableContextBar({
  ref,
  minWidth = MIN_WIDTHS.DEFAULT,
  isActive = false,
}: Args) {
  const [width, setWidth] = useLocalStorageState<number>(
    `${STORAGE_KEY}_${minWidth}`,
  );
  const [isManuallySized, setIsManuallySized] = useLocalStorageState<boolean>(
    `${MANUAL_STORAGE_KEY}_${minWidth}`,
    { defaultValue: false },
  );
  const [autoWidth, setAutoWidth] = useState<number | null>(null);
  const [isDragging, setIsDragging] = React.useState(false);
  const contentRef = useRef<HTMLDivElement>(null);
  const hasMigratedManualFlag = useRef(false);

  const effectiveWidth = isManuallySized
    ? width ?? minWidth
    : autoWidth ?? width ?? minWidth;

  // Width saved before manual tracking was added — treat as user preference.
  useEffect(() => {
    if (
      hasMigratedManualFlag.current ||
      isManuallySized ||
      width === undefined
    ) {
      return;
    }
    hasMigratedManualFlag.current = true;
    setIsManuallySized(true);
  }, [isManuallySized, setIsManuallySized, width]);

  useEffect(() => {
    if (!isActive) {
      setAutoWidth(null);
    }
  }, [isActive]);

  const fitContentWidth = useCallback(() => {
    if (isManuallySized || !isActive) {
      return;
    }

    const content = contentRef.current;
    const root = ref.current;
    if (!content || !root) {
      return;
    }

    const appliedWidth =
      Math.round(root.getBoundingClientRect().width) || effectiveWidth;
    const requiredWidth = content.scrollWidth;
    const isContentWiderThanBar =
      requiredWidth > appliedWidth + OVERFLOW_TOLERANCE;

    if (!isContentWiderThanBar) {
      return;
    }

    const maxWidth = Math.ceil(window.innerWidth / 3);
    const nextWidth = Math.min(
      maxWidth,
      Math.max(minWidth, appliedWidth + WIDTH_STEP, requiredWidth),
    );

    if (nextWidth > appliedWidth) {
      setAutoWidth(nextWidth);
    }
  }, [effectiveWidth, isActive, isManuallySized, minWidth, ref]);

  useLayoutEffect(() => {
    if (isManuallySized || !isActive) {
      return;
    }

    const content = contentRef.current;
    if (!content) {
      return;
    }

    fitContentWidth();

    const observer = new ResizeObserver(() => {
      fitContentWidth();
    });
    observer.observe(content);

    return () => observer.disconnect();
  }, [fitContentWidth, isActive, isManuallySized]);

  if (!document) {
    return {
      isDragging,
      width: effectiveWidth,
      contentRef,
      handle: null,
    };
  }

  const onResizePointerDown = (event: React.PointerEvent) => {
    setIsDragging(true);
    setIsManuallySized(true);
    setAutoWidth(null);
    const startX = event.pageX;
    const initialWidth = effectiveWidth || ref?.current?.clientWidth;
    document.body.style.userSelect = 'none';

    const onPointerMove = (event: PointerEvent) => {
      const delta = startX - event.pageX;
      const maxWidth = Math.ceil(window.innerWidth / 3);

      if (delta < 0) {
        setWidth(Math.max(minWidth, (initialWidth || 0) + delta));
      } else {
        setWidth(Math.min(maxWidth, (initialWidth || 0) + delta));
      }
    };

    const onPointerUp = () => {
      setIsDragging(false);
      document.removeEventListener('pointermove', onPointerMove);
      document.removeEventListener('pointerup', onPointerUp);
      document.body.style.userSelect = '';
    };

    document.addEventListener('pointermove', onPointerMove);
    document.addEventListener('pointerup', onPointerUp);
  };

  return {
    isDragging,
    width: effectiveWidth,
    contentRef,
    handle: (
      <div
        className={css['resizable-handle']}
        onPointerDown={onResizePointerDown}
      />
    ),
  };
}
